import { ObjectRule } from '@/classes/rules/ObjectRule';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { TextRule } from '@/classes/rules/TextRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { OptionsRule } from '@/classes/rules/OptionsRule';
import _scenario from '@/helpers/scenarioHelper';
import { BooleanRule } from '@/classes/rules/BooleanRule';
import { EnumRule } from '@/classes/rules/EnumRule';
import { CurrencyEnum, InjectionModelRef, RegionEnum, MetropoleRegionEnum, ReferenceScenarioEnum, CaseTypeRefScenario } from '@/dto/scenario/ScenarioReference';
import { buildLoadRules } from '@/helpers/rulesModules/buildLoadRules';
import { buildBatteryRules } from '@/helpers/rulesModules/buildBatteryRules';
import { buildGridRules } from '@/helpers/rulesModules/buildGridRules';
import { buildGensetRules } from '@/helpers/rulesModules/buildGensetRules';
import { buildSizingRules } from '@/helpers/rulesModules/buildSizingRules';
import { RuleFieldMessage } from '@/classes/rules/RuleFieldMessage';
import { ProjectTypeRef } from '@/dto/scenario/ProjectReference';
import { SizingAlgorithmRef } from '@/dto/scenario/sizing/SizingReference';
import { INestedRule } from '@/classes/rules/INestedRule';
import { buildRenFrPvRules } from './ren-fr/buildRenFrPvRules';
import { buildRenFrWindRules } from './ren-fr/buildRenFrWindRules';
import { buildRevenueRules } from './ren-fr/buildRevenueRules';
import { buildCapexRules } from './ren-fr/buildCapexRules';
import { buildOpexRules } from './ren-fr/buildOpexRules';
import { buildStructureFinanceRules } from './ren-fr/buildStructureFinanceRules';
import { DateRule } from '@/classes/rules/DateRule';

interface Dependencies {
  buildPvRules: () => INestedRule;
  buildEcoRules: () => INestedRule;
}

export const buildScenarioRules = ({ buildPvRules, buildEcoRules }: Dependencies) => new ObjectRule<ScenarioExt>('scenario', [
  new TextRule('name', /.*/s),

  new MinMaxRule('totalEnergiesShareholdingInterest'),

  new MinMaxRule('durationYear', 1, 25, 'years')
    .withDefaultValue(() => new DefaultValue(25)),

  new EnumRule('irc', CurrencyEnum)
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new TextRule('mocInflation')
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new TextRule('ircInflation')
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new TextRule('mocFx')
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new TextRule('ircFx')
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new MinMaxRule('regionalFdThreshold')
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new MinMaxRule('fx')
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)),

  new OptionsRule<string>('configuration', _scenario.validConfigurations),

  new BooleanRule('hasGrid'),

  new BooleanRule('hasInjection')
    .withIsAvailable(scenario => (scenario.grid && scenario.grid.hasGrid24) || false),

  new BooleanRule('hasBattery')
    .withIsAvailable(scenario => {
      return !scenario.hasGrid || !scenario.hasInjection || !(scenario.grid && scenario.grid.hasGrid24);
    }),

  new BooleanRule('hasGenset')
    .withIsAvailable(scenario => !scenario.hasInjection && !(scenario.grid && scenario.grid.hasGrid24)),

  new EnumRule('injectionModelRef', InjectionModelRef)
    .withIsAvailable(scenario => !!scenario.hasInjection),

  // for Ren Fr project
  new EnumRule('referenceScenario', ReferenceScenarioEnum)
    .withIsAvailable(scenario => false),
  new TextRule('analyticalCode', /.*/s)
    .withIsAvailable(scenario => false),
  new TextRule('spv', /.*/s)
    .withIsAvailable(scenario => false),
  new TextRule('projectManager')
    .withIsAvailable(scenario => false),
  new DateRule('startDate')
    .withIsAvailable(scenario => isRenFranceProject(scenario)),
  new MinMaxRule('constructionDuration')
    .withIsAvailable(scenario => isRenFranceProject(scenario)),
  new MinMaxRule('exploitationDuration')
    .withIsAvailable(scenario => isRenFranceProject(scenario)),
  new TextRule('energy')
    .withIsAvailable(scenario => isRenFranceProject(scenario)),
  new EnumRule('technology', CaseTypeRefScenario)
    .withIsAvailable(scenario => isRenFranceProject(scenario)),
  new EnumRule('region', RegionEnum)
    .withIsAvailable(scenario => isRenFranceProject(scenario)),
  new EnumRule('metropoleRegion', MetropoleRegionEnum)
    .withIsAvailable(scenario => isRenFranceProject(scenario) && scenario.region === RegionEnum.Métropole),

  buildLoadRules(),
  buildBatteryRules(),
  buildGridRules(),
  buildPvRules(),
  buildGensetRules(),
  buildEcoRules(),
  buildSizingRules(),
  // For ren fr project
  buildRenFrPvRules(),
  buildRenFrWindRules(),
  buildRevenueRules(),
  buildCapexRules(),
  buildOpexRules(),
  buildStructureFinanceRules(),

  new ObjectRule<any>('executiveReport'),

  new ObjectRule<any>('fullReport')
]).withCustomCheck((scenario, errorMessages) => {
  const messages = errorMessages || [];
  if (scenario.eco && !scenario.eco.hasBreakdownCapex && scenario.sizing && scenario.sizing.hasBatteryRange && scenario.hasBattery) {
    messages.push(new RuleFieldMessage('sizing.batterRangeAlert', [
      'scenario.sizing.hasBatteryRange'
    ]));
  }
  pvGreaterThanBattery(scenario, messages);
  batterySocGridAndGeneratorVerificationFunction(scenario, messages);
  return messages.length === 0;
})
  .withDefaultValue(() => new DefaultValue(new ScenarioExt()));

function pvGreaterThanBattery(scenario: ScenarioExt, messages: RuleFieldMessage[]) {
  if (scenario.sizing === null) {
    return;
  }
  if (!scenario.hasBattery || (scenario.project?.typeRef !== ProjectTypeRef.gas_station_project)) {
    return messages.length === 0;
  }
  if (scenario.sizing.hasBatteryRange === false &&
    scenario.sizing.batteryCapacity !== null &&
    scenario.sizing.pvMax !== null &&
    scenario.sizing.batteryCapacity < scenario.sizing.pvMax) {
    messages.push(new RuleFieldMessage('scenario.sizing.errors.batteryCapacity_under_pvMax', [
      'scenario.sizing.batteryCapacity',
      'scenario.sizing.pvMax'
    ]));
  }
  if (scenario.sizing.hasBatteryRange === true &&
    scenario.sizing.batteryMin !== null &&
    scenario.sizing.pvMin !== null &&
    scenario.sizing.batteryMin < scenario.sizing.pvMin) {
    messages.push(new RuleFieldMessage('scenario.sizing.errors.batteryMin_under_pvMin', [
      'scenario.sizing.batteryMin',
      'scenario.sizing.pvMin'
    ]));
  }
  if (scenario.sizing.hasBatteryRange === true &&
    scenario.sizing.batteryMax !== null &&
    scenario.sizing.pvMax !== null &&
    scenario.sizing.batteryMax < scenario.sizing.pvMax) {
    messages.push(new RuleFieldMessage('scenario.sizing.errors.batteryMax_under_pvMax', [
      'scenario.sizing.batteryMax',
      'scenario.sizing.pvMax'
    ]));
  }
}

function batterySocGridAndGeneratorVerificationFunction(scenario: ScenarioExt, messages: RuleFieldMessage[]) {
  if (!scenario || !scenario.battery) {
    return null;
  }
  if (scenario.battery.isRechargeableByGenerator && scenario.battery.isRechargeableByGrid &&
    scenario.battery.socMaxChargeGen !== null &&
    scenario.battery.socMaxChargeGrid !== null &&
    (scenario.sizing?.algorithmRef === SizingAlgorithmRef.algo_v3_hybrid || scenario.sizing?.algorithmRef === SizingAlgorithmRef.algo_multi_genset)) {
    if (scenario.battery.globalSocMaxPercentage === null || scenario.battery.socMinPercentage === null) {
      messages.push(new RuleFieldMessage('Define socs', [
        'scenario.battery.socMaxChargeGrid',
        'scenario.battery.socMaxChargeGen'
      ]));
    } else {
      if (scenario.hasGrid && scenario.hasGenset && scenario.battery.socMaxChargeGrid < scenario.battery.socMaxChargeGen) {
        messages.push(new RuleFieldMessage('scenario.battery.errors.socMaxChargeGrid_under_socMaxChargeGen', [
          'scenario.battery.socMaxChargeGrid',
          'scenario.battery.socMaxChargeGen'
        ]));
      }
      if (scenario.hasGenset && scenario.battery.socMaxChargeGen > scenario.battery.globalSocMaxPercentage) {
        messages.push(new RuleFieldMessage('scenario.battery.errors.socMaxChargeGen_above_socMax', [
          'scenario.battery.socMaxChargeGen'
        ]));
      }
      if (scenario.hasGrid && scenario.battery.socMaxChargeGrid > scenario.battery.globalSocMaxPercentage) {
        messages.push(new RuleFieldMessage('scenario.battery.errors.socMaxChargeGrid_above_socMax', [
          'scenario.battery.socMaxChargeGrid'
        ]));
      }
      if (scenario.hasGenset && scenario.battery.socMaxChargeGen <= scenario.battery.socMinPercentage) {
        messages.push(new RuleFieldMessage('scenario.battery.errors.socMaxChargeGen_under_socMin', [
          'scenario.battery.socMaxChargeGen'
        ]));
      }
      if (scenario.hasGrid && scenario.battery.socMaxChargeGrid <= scenario.battery.socMinPercentage) {
        messages.push(new RuleFieldMessage('scenario.battery.errors.socMaxChargeGrid_under_socMin', [
          'scenario.battery.socMaxChargeGrid'
        ]));
      }
    }
  }
}

function isRenFranceProject(scenario: ScenarioExt): boolean {
  return (scenario.project?.typeRef === ProjectTypeRef.ren_fr_pv_project || scenario.project?.typeRef === ProjectTypeRef.ren_fr_wind_project) &&
    scenario.hasInjectionType(InjectionModelRef.total_injection);
}
